import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Send, FileCheck2, Users, ArrowUpRight, Mail, FileText, Plus, Building2,
    ShieldAlert, TrendingUp, Receipt, Settings2, RotateCcw, CheckCircle2,
} from "lucide-react";
import { SaferReverifyCard } from "@/components/SaferReverifyCard";
import { RenewalRadarCard } from "@/components/RenewalRadarCard";
import { InvoiceAgingCard } from "@/components/InvoiceAgingCard";
import { PaymentActivityCard } from "@/components/PaymentActivityCard";
import {
    PendingSignaturesCard,
    CoiExpiringCard,
    ActiveRentalsCard,
    SchedulesAtRiskCard,
    AutoChargeAttemptsSummaryCard,
    CronHealthCard,
    EmailFailuresCard,
    ImpersonationsCard,
} from "@/components/cards/DashboardExtraCards";
import { CoiExpiringSoonCard } from "@/components/cards/CoiExpiringSoonCard";  // Phase COI-REFRESH (Part B)
import UninvoicedReleasesCard from "@/components/cards/UninvoicedReleasesCard";  // Phase J.8 (2026-08-01)
import { AdminDashboardTabs } from "@/components/AdminDashboardTabs";
import { DashboardCardShell } from "@/components/DashboardCardShell";
import { TodayTabPanel } from "@/components/TodayTabPanel";
import { UnscheduledRentalsBanner } from "@/components/UnscheduledRentalsBanner";
import {
    DASHBOARD_TABS,
    DASHBOARD_CARDS,
    DEFAULT_TAB_ID,
    DEFAULT_HIDDEN_CARD_KEYS,
    cardsForTab,
} from "@/lib/dashboardCards";

const SUPER_ADMIN_ONLY_TAB_IDS = new Set(["system"]);
const FRESH_LOGIN_FLAG = "rp_admin_dash_visited";

export default function AdminDashboardPage() {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const isSuperAdmin = user?.role === "super_admin";
    // S1 follow-up — hide the "System & Activity" tab from regular admins.
    // Every card in this tab is already super_admin-only on the backend, so
    // the tab was effectively dead UI for them.
    const visibleTabs = useMemo(
        () => DASHBOARD_TABS.filter(
            (t) => isSuperAdmin || !SUPER_ADMIN_ONLY_TAB_IDS.has(t.id),
        ),
        [isSuperAdmin],
    );
    const visibleTabIdSet = useMemo(
        () => new Set(visibleTabs.map((t) => t.id)),
        [visibleTabs],
    );

    // ----- stats + prefs state -----
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);
    const [hiddenCards, setHiddenCards] = useState(null); // null while loading
    const [customizeMode, setCustomizeMode] = useState(false);

    // ----- tab resolution -----
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const queryTab = queryParams.get("tab");
    const isFreshLogin = typeof window !== "undefined" && !sessionStorage.getItem(FRESH_LOGIN_FLAG);

    // Resolve initial active tab.
    let resolvedTab;
    if (isFreshLogin) {
        resolvedTab = DEFAULT_TAB_ID;
    } else if (queryTab && visibleTabIdSet.has(queryTab)) {
        resolvedTab = queryTab;
    } else {
        resolvedTab = DEFAULT_TAB_ID;
    }

    // Normalize URL to reflect the resolved tab (and mark this session as
    // "visited" so subsequent navigations preserve the user's chosen tab).
    useEffect(() => {
        if (typeof window !== "undefined") {
            sessionStorage.setItem(FRESH_LOGIN_FLAG, "1");
        }
        if (queryTab !== resolvedTab) {
            const next = new URLSearchParams(location.search);
            next.set("tab", resolvedTab);
            navigate(
                { pathname: location.pathname, search: `?${next.toString()}` },
                { replace: true },
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const activeTabId = queryTab && visibleTabIdSet.has(queryTab) ? queryTab : resolvedTab;

    const switchTab = (tabId) => {
        const next = new URLSearchParams(location.search);
        next.set("tab", tabId);
        navigate(
            { pathname: location.pathname, search: `?${next.toString()}` },
            { replace: false },
        );
    };

    // ----- data loading -----
    const refreshStats = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/stats");
            setStats(data);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }, []);

    useEffect(() => {
        (async () => {
            await refreshStats();
            setStatsLoading(false);
        })();
    }, [refreshStats]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/dashboard-preferences");
                setHiddenCards(data.hidden_cards || []);
            } catch (err) {
                // Fallback to local defaults if the prefs endpoint isn't
                // reachable — keeps the dashboard rendering.
                console.warn("dashboard-preferences GET failed", err);
                setHiddenCards([...DEFAULT_HIDDEN_CARD_KEYS]);
            }
        })();
    }, []);

    const persistHidden = useCallback(async (next) => {
        try {
            const { data } = await api.patch("/admin/dashboard-preferences", {
                hidden_cards: next,
            });
            setHiddenCards(data.hidden_cards || []);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }, []);

    const onToggleCard = useCallback((cardKey, shouldHide) => {
        const current = new Set(hiddenCards || []);
        if (shouldHide) current.add(cardKey);
        else current.delete(cardKey);
        const next = Array.from(current);
        setHiddenCards(next);
        persistHidden(next);
    }, [hiddenCards, persistHidden]);

    const onResetTab = useCallback(async (tabId) => {
        const cardsInTab = cardsForTab(tabId).map((c) => c.key);
        const tabDefaults = cardsForTab(tabId)
            .filter((c) => c.defaultHidden)
            .map((c) => c.key);
        // Drop existing entries for this tab + re-add the tab's defaults.
        const next = (hiddenCards || []).filter((k) => !cardsInTab.includes(k));
        tabDefaults.forEach((k) => { if (!next.includes(k)) next.push(k); });
        setHiddenCards(next);
        await persistHidden(next);
        toast.success(`Reset "${DASHBOARD_TABS.find((t) => t.id === tabId)?.label}" to defaults.`);
    }, [hiddenCards, persistHidden]);

    // ----- cards loading guard -----
    const hiddenSet = useMemo(() => new Set(hiddenCards || []), [hiddenCards]);
    const isHidden = (key) => hiddenSet.has(key);
    const prefsReady = hiddenCards !== null;

    // ----- legacy stat-tile rendering (used by Onboarding/Contracts/Rentals/Billing/System) -----
    const statCard = useCallback((spec) => {
        const Icon = spec.icon;
        return (
            <Link
                key={spec.key}
                to={spec.link}
                data-testid={`admin-card-link-${spec.key}`}
                className="block h-full"
            >
                <Card data-testid={`admin-card-${spec.key}`} className="h-full hover:border-slate-300 transition-colors">
                    <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
                        <div className="space-y-1.5">
                            <CardTitle className="text-base font-semibold text-slate-900">
                                {spec.title}
                            </CardTitle>
                            <CardDescription className="text-sm text-slate-500">
                                {spec.description}
                            </CardDescription>
                        </div>
                        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-md ${spec.accent}`}>
                            <Icon size={18} />
                        </span>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-end justify-between">
                            <span className="font-display text-4xl font-bold tracking-tight text-slate-900" data-testid={`admin-card-value-${spec.key}`}>
                                {statsLoading ? "…" : spec.value}
                            </span>
                            <span className="inline-flex items-center gap-1 text-sm font-medium text-blue-900">
                                {spec.cta} <ArrowUpRight size={14} />
                            </span>
                        </div>
                    </CardContent>
                </Card>
            </Link>
        );
    }, [statsLoading]);

    // ----- stat-tile spec lookup by key -----
    const statSpecs = useMemo(() => ({
        "pending-review": {
            key: "pending-review", title: "Pending review",
            description: "Submitted applications awaiting your review.",
            icon: FileText, accent: "bg-amber-50 text-amber-900",
            value: stats?.pending_review ?? "—",
            link: "/admin/submissions?status=submitted", cta: "Open submissions",
        },
        "approved": {
            key: "approved", title: "Approved",
            description: "Applications you've approved.",
            icon: FileCheck2, accent: "bg-emerald-50 text-emerald-900",
            value: stats?.submissions_approved ?? "—",
            link: "/admin/submissions?status=approved", cta: "View",
        },
        "rejected": {
            key: "rejected", title: "Rejected",
            description: "Applications you've rejected.",
            icon: FileText, accent: "bg-red-50 text-red-700",
            value: stats?.submissions_rejected ?? "—",
            link: "/admin/submissions?status=rejected", cta: "View",
        },
        "invites": {
            key: "invites", title: "Invites pending",
            description: "Sent and waiting for the customer.",
            icon: Send, accent: "bg-blue-50 text-blue-900",
            value: stats?.invites_pending ?? "—",
            link: "/admin/invites", cta: "Manage invites",
        },
        "drafts": {
            key: "drafts", title: "Customer drafts",
            description: "In-progress applications.",
            icon: FileText, accent: "bg-slate-100 text-slate-800",
            value: stats?.submissions_drafts ?? "—",
            link: "/admin/submissions?status=draft", cta: "View",
        },
        "customers": {
            key: "customers", title: "Customers",
            description: stats ? `${stats?.archived_customers ?? 0} archived` : "Active customer profiles.",
            icon: Building2, accent: "bg-blue-50 text-blue-900",
            value: stats?.active_customers ?? "—",
            link: "/admin/customers", cta: "Open customers",
        },
        "safer-risk": {
            key: "safer-risk", title: "SAFER risk flags",
            description: "Active customers flagged by FMCSA SAFER",
            icon: ShieldAlert,
            accent: (stats?.customers_with_safer_risk ?? 0) > 0 ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700",
            value: stats?.customers_with_safer_risk ?? "—",
            link: "/admin/customers?safer_risk=1",
            cta: (stats?.customers_with_safer_risk ?? 0) > 0 ? "Review flagged" : "All clear",
        },
        "renewal-acceptance": {
            key: "renewal-acceptance", title: "Renewal acceptance rate",
            description: stats
                ? `${stats?.renewals_signed_90d ?? 0} signed of ${stats?.renewals_sent_90d ?? 0} sent · last 90d`
                : "Trailing 90-day signing rate",
            icon: TrendingUp,
            accent:
                (stats?.renewal_acceptance_rate_90d ?? 0) >= 70 ? "bg-emerald-50 text-emerald-900" :
                (stats?.renewal_acceptance_rate_90d ?? 0) >= 40 ? "bg-amber-50 text-amber-900" :
                (stats?.renewal_acceptance_rate_90d ?? 0) > 0 ? "bg-red-50 text-red-700" :
                "bg-slate-100 text-slate-700",
            value: stats ? `${(stats.renewal_acceptance_rate_90d ?? 0).toFixed(1)}%` : "—",
            link: "/admin/renewals/analytics", cta: "View analytics",
        },
        "invoices": {
            key: "invoices", title: "Invoices",
            description: "Browse issued invoices and their status.",
            icon: Receipt, accent: "bg-emerald-50 text-emerald-900",
            value: "→",
            link: "/admin/invoices", cta: "Open invoices",
        },
        "statements": {
            key: "statements", title: "Statements",
            description: "Consolidate multiple invoices into one ACH charge.",
            icon: Receipt, accent: "bg-indigo-50 text-indigo-900",
            value: "→",
            link: "/admin/statements", cta: "Open statements",
        },
        "mock-emails": {
            key: "mock-emails", title: "Mock email outbox",
            description: "Latest invite + reopen emails the system has logged.",
            icon: Mail, accent: "bg-slate-100 text-slate-800",
            value: "→",
            link: "/admin/mock-emails", cta: "Open outbox",
        },
        "admins": {
            key: "admins", title: "Admins",
            description: "Active admin team members.",
            icon: Users, accent: "bg-amber-50 text-amber-900",
            value: stats?.admins ?? "—",
            link: "/admin/admins", cta: "Manage admins",
        },
    }), [stats]);

    // Helper to render a single card by key wrapped in DashboardCardShell.
    const renderCard = (cardKey, opts = {}) => {
        const meta = DASHBOARD_CARDS.find((c) => c.key === cardKey);
        if (!meta) return null;
        if (meta.requiresSuperAdmin && user?.role !== "super_admin") return null;

        let inner = null;
        switch (cardKey) {
            case "safer-reverify":
                inner = <SaferReverifyCard stats={stats} onRefreshStats={refreshStats} />;
                break;
            case "renewal-radar":
                inner = <RenewalRadarCard />;
                break;
            case "invoice-aging":
                inner = <InvoiceAgingCard />;
                break;
            case "payment-activity":
                inner = <PaymentActivityCard />;
                break;
            case "pending-signatures":
                inner = <PendingSignaturesCard stats={stats} />;
                break;
            case "coi-expiring":
                inner = <CoiExpiringCard stats={stats} />;
                break;
            case "coi-expiring-soon-widget":
                inner = <CoiExpiringSoonCard />;
                break;
            case "active-rentals":
                inner = <ActiveRentalsCard stats={stats} />;
                break;
            case "schedules-at-risk":
                inner = <SchedulesAtRiskCard />;
                break;
            case "uninvoiced-releases":
                inner = <UninvoicedReleasesCard />;
                break;
            case "auto-charge-attempts-summary":
                inner = <AutoChargeAttemptsSummaryCard />;
                break;
            case "cron-health":
                inner = <CronHealthCard />;
                break;
            case "email-failures":
                inner = <EmailFailuresCard />;
                break;
            case "impersonations-7d":
                inner = <ImpersonationsCard />;
                break;
            default: {
                const spec = statSpecs[cardKey];
                if (!spec) return null;
                inner = statCard(spec);
            }
        }

        return (
            <DashboardCardShell
                key={cardKey}
                cardKey={cardKey}
                label={meta.label}
                hidden={isHidden(cardKey)}
                customizeMode={customizeMode}
                onToggle={onToggleCard}
                wide={opts.wide}
            >
                {inner}
            </DashboardCardShell>
        );
    };

    // ----- per-tab rendering -----
    const renderTabBody = () => {
        if (!prefsReady) {
            return (
                <div className="mt-10 text-sm text-slate-500" data-testid="admin-dashboard-loading">
                    Loading dashboard…
                </div>
            );
        }
        switch (activeTabId) {
            case "today":
                return (
                    <div className="mt-8">
                        <TodayTabPanel />
                    </div>
                );
            case "onboarding":
                return (
                    <TabGrid testId="tab-panel-onboarding">
                        {renderCard("pending-review")}
                        {renderCard("invites")}
                        {renderCard("drafts")}
                        {renderCard("customers")}
                        {renderCard("approved")}
                        {renderCard("rejected")}
                    </TabGrid>
                );
            case "contracts":
                return (
                    <TabGrid testId="tab-panel-contracts">
                        {renderCard("pending-signatures")}
                        {renderCard("coi-expiring")}
                        {renderCard("safer-risk")}
                        {renderCard("safer-reverify")}
                        {renderCard("coi-expiring-soon-widget", { wide: true })}
                    </TabGrid>
                );
            case "rentals":
                return (
                    <TabGrid testId="tab-panel-rentals">
                        {renderCard("active-rentals")}
                        {renderCard("renewal-acceptance")}
                        {renderCard("renewal-radar")}
                    </TabGrid>
                );
            case "billing":
                return (
                    <TabGrid testId="tab-panel-billing">
                        {renderCard("invoices")}
                        {renderCard("statements")}
                        {renderCard("invoice-aging")}
                        {renderCard("schedules-at-risk")}
                        {renderCard("uninvoiced-releases")}
                    </TabGrid>
                );
            case "payments":
                return (
                    <TabGrid testId="tab-panel-payments">
                        {renderCard("payment-activity", { wide: true })}
                        {renderCard("auto-charge-attempts-summary")}
                    </TabGrid>
                );
            case "system":
                return (
                    <TabGrid testId="tab-panel-system">
                        {renderCard("mock-emails")}
                        {renderCard("admins")}
                        {renderCard("email-failures")}
                        {renderCard("impersonations-7d")}
                        {renderCard("cron-health", { wide: true })}
                    </TabGrid>
                );
            default:
                return null;
        }
    };

    return (
        <DashboardShell>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 fade-up">
                <div>
                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900 mb-3">
                        {user?.role === "super_admin" ? "Super admin" : "Admin"} workspace
                    </p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                        Hello, {user?.full_name?.split(" ")[0] || "there"}.
                    </h1>
                    <p className="mt-3 max-w-2xl text-base text-slate-600 leading-relaxed">
                        Send tailored invite links, monitor customer progress, and review submitted applications.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        type="button"
                        variant={customizeMode ? "default" : "outline"}
                        className={customizeMode ? "bg-slate-900 hover:bg-slate-800 gap-2" : "gap-2"}
                        onClick={() => setCustomizeMode((v) => !v)}
                        data-testid="dashboard-customize-toggle"
                    >
                        {customizeMode ? <CheckCircle2 size={16} /> : <Settings2 size={16} />}
                        {customizeMode ? "Done customizing" : "Customize"}
                    </Button>
                    <Button asChild className="bg-blue-900 hover:bg-blue-800 gap-2">
                        <Link to="/admin/invites/new" data-testid="dashboard-new-invite">
                            <Plus size={16} /> New invite
                        </Link>
                    </Button>
                </div>
            </div>

            <UnscheduledRentalsBanner isSuperAdmin={isSuperAdmin} />

            <div className="mt-8">
                <AdminDashboardTabs
                    tabs={visibleTabs}
                    activeTabId={activeTabId}
                    onChange={switchTab}
                />
            </div>

            {customizeMode && (
                <div
                    data-testid="dashboard-customize-banner"
                    className="mt-4 rounded-md border border-blue-200 bg-blue-50/60 px-4 py-2.5 text-sm text-blue-900 flex items-center justify-between gap-3"
                >
                    <span>
                        Customize mode is on. Use the <span className="font-semibold">Hide</span>/<span className="font-semibold">Show</span> pills on each card.
                        Hidden cards stay hidden on this tab even after you leave customize mode.
                    </span>
                </div>
            )}

            {renderTabBody()}

            {customizeMode && activeTabId !== "today" && (
                <div className="mt-6 flex justify-end">
                    <button
                        type="button"
                        onClick={() => onResetTab(activeTabId)}
                        data-testid={`dashboard-reset-tab-${activeTabId}`}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 hover:text-slate-900 underline-offset-4 hover:underline"
                    >
                        <RotateCcw size={12} />
                        Reset this tab to defaults
                    </button>
                </div>
            )}
        </DashboardShell>
    );
}

function TabGrid({ children, testId }) {
    return (
        <section
            data-testid={testId || "admin-stats"}
            className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
        >
            {/* Filter falsy entries so empty (role-gated) renders don't break grid spacing. */}
            {React.Children.toArray(children).filter(Boolean)}
        </section>
    );
}
